import { authorMutationWrites, parseMutationCdc } from './author-mutation-fanout'
import { pageLifecycleStatements } from './page-relations-derivation'
import { type DerivationArgs } from './card-derivation'
import { setWorkspaceSessionUser, workspaceSessionUser } from './session-user-db-task'
import { removedQuestionParts } from './page-read'
import { pageSourceColumns } from './workspace-tables'
// INV-1: bucket (network) calls run only after the page write transaction commits.
import { crdt, type StateJSON } from '@w/crdt'
import { blockId } from '@w/content-blocks'
import { type Tel } from '@w/tel'
import { safe } from '@w/utils'
import { s } from '@w/schema'
import { searchKey } from '../search/search-key'
import { newPageDocument, assertPageStructure, pageScalarValues, projectPage, readPageDocument, type PageScalarFields } from './page-document'
import type { WorkspaceDb, WorkspacePage } from './workspace-tables'
import { authoredSourceBatch } from './authored-source-batch'
import { assertPageContentSize } from './page-content-size'
import type { SourceTransaction, TursoWorkerDatabaseDriver } from '@w/db-turso-browser-adapter/worker'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const appliedSchema = s.object({ applied: s.string() })
const presentSchema = s.object({ present: s.number() })
const admittedSchema = s.object({ admitted: s.number() })

export type CommitPageEditInput = {
  spaceId?: string
  /** Creation placement only; content edits never move an existing page. */
  parentId?: string | null

  bucket?: {
    delete(path: string): Promise<void>
    restore(path: string): Promise<void>
  }
  pageId: string
  frame: Uint8Array
  /** The author's changes in `frame` all edited text (crdt Change.textOnly): the commit merges it once, without a
   * preflight merge (authored-source-batch mergeOnce); the engine still refuses a merge that applies rows. */
  prose?: true
  fields?: PageScalarFields
  /** Validated CLI scalar columns; generated columns remain engine-owned. */
  columns?: Partial<Omit<WorkspacePage, 'id' | 'data'>>
  now: Date
  /** Required when reconciling questions: the workspace DB contains per-user rows. */
  userId?: string
  /** Trace context for this edit. */
  tel: Tel
}

// One typed edit is one trace: the write transaction and the replication bootstrap
// check it triggers both nest under `page.commit`, never as sibling root traces.
export function commitPageEdit(db: WorkspaceDb, input: CommitPageEditInput) {
  return commitPageEdits(db, [input]).then(() => ({ id: input.pageId }))
}

/** Ordered page writes are captured and replayed as one user command. */
/** Statements committed in the same transaction as the page writes (db-crdt-sync :37: re-expressed work is marked done atomically). */
export type AlsoStatement = { sql: string; params: unknown[] }

export function commitPageEdits(db: WorkspaceDb, inputs: readonly CommitPageEditInput[], options?: { transactionBytes?: number; plannedPageIds?: ReadonlySet<string>; also?: readonly AlsoStatement[] }) {
  const first = inputs[0]
  if (!first) return Promise.resolve()
  return first.tel.span({ name: 'page.commit', target: 'db', input: { pageId: first.pageId } }, async spanTel => {
    const scopedDb = db.withTel(spanTel)
    const documents = new Map<string, ReturnType<typeof crdt.load>>()
    // Each frame is decoded (B9) once here; the document, the write and the source batch reuse it.
    const frames: StateJSON[] = []
    const childPageIds: string[][] = []
    const created = new Map<number, { doc: ReturnType<typeof crdt.load>; json: ReturnType<ReturnType<typeof crdt.load>['toJSON']> }>()
    const driver = safe(scopedDb.driver) as TursoWorkerDatabaseDriver & { flushInvalidations?(): void }
    const sourceTransaction = driver.sourceTransaction ?? (driver.failures as (typeof driver.failures & { sourceTransaction?: SourceTransaction }))?.sourceTransaction
    const [state] = sourceTransaction ? await driver.query<{ failure_workspace: string | null; failure_principal: string | null; failure_generation: bigint | number | null; identity: Uint8Array }>('SELECT failure_workspace,failure_principal,failure_generation,identity FROM _turso_replication_state WHERE id=1') : []
    const scope = state && state.failure_workspace !== null && state.failure_principal !== null && state.failure_generation !== null
      ? { workspace: state.failure_workspace, principal: state.failure_principal, generation: BigInt(state.failure_generation), replica: new Uint8Array(state.identity) }
      : undefined
    for (const [index, input] of inputs.entries()) {
      if (input.frame.length === 0) {
        // A fields-only change (soft delete, title, archive) writes scalar columns: an empty frame means no CRDT
        // change (crdt facade: a no-op edit encodes 0 bytes). The stored `data` stays byte-equal; the checks below
        // still run on the stored state.
        let document = documents.get(input.pageId)
        if (!document) {
          const [stored] = await safe(scopedDb.driver).query<{ data: Uint8Array }>('SELECT data FROM pages WHERE id=?', [input.pageId])
          if (!stored) throw new Error('Expected snapshot')
          document = crdt.load(stored.data)
          documents.set(input.pageId, document)
        }
        frames.push(fieldsOnlyFrame)
        childPageIds.push([])
        continue
      }
      const decoded = crdt.decodeFrame(input.frame)
      const frame = decoded.json
      frames.push(frame)
      const opened = frame.baseline ? decoded.open() : undefined
      if (frame.baseline && frame.changes.length === 0) {
        assertPageStructure(frame.baseline.json)
        if ((await safe(scopedDb.driver).query('SELECT id FROM pages WHERE id=?', [input.pageId])).length > 0) throw new Error('Pure baseline cannot update an existing page; send authored CRDT edits')
      }
      // The native source batch checks the engine's one merge of an edit frame (authored-source-batch.ts).
      if (sourceTransaction && scope && !opened) { childPageIds.push([]); continue }
      let document = documents.get(input.pageId)
      // Page blocks the frame adds (insert, retype to page, pageId set): the native batch plans their child rows.
      let before: ReadonlySet<string> | undefined
      if (opened) document = opened
      else if (document) { before = pageBlockIds(document.toJSON()); document.applyUpdate(decoded.update()) }
      else {
        const previous = await safe(scopedDb.driver).query<{ data: Uint8Array }>('SELECT data FROM pages WHERE id=?', [input.pageId])
        // The stored row is the merge base: the size and structure checks need the merged state. A missing row
        // opens the frame `decodeFrame` already parsed (a non-snapshot frame throws 'Expected snapshot', as before).
        if (previous[0]) {
          document = crdt.load(previous[0].data)
          before = pageBlockIds(document.toJSON())
          document.applyUpdate(decoded.update())
        } else document = decoded.open()
      }
      documents.set(input.pageId, document)
      const json = document.toJSON()
      childPageIds.push(before ? [...pageBlockIds(json)].filter(id => !before.has(id)) : [])
      assertPageStructure(json)
      assertPageContentSize(input.pageId, json as unknown as Parameters<typeof assertPageContentSize>[1])
      // A creation writes this exact state; the write reuses it instead of decoding the frame and the row again.
      if (opened) created.set(index, { doc: opened, json })
    }
    // Later frames for a page mutate its Doc in place; only a creation that is the page's last frame keeps its state.
    for (const [index] of created) if (inputs.slice(index + 1).some(later => later.pageId === safe(inputs[index]).pageId)) created.delete(index)
    const sessionUserId = await workspaceSessionUser(safe(scopedDb.driver))
    const writingUser = sessionUserId ?? first.userId
    // The server refuses editedBy !== caller; fail before publishing an optimistic edit.
    if (inputs.some(input => input.userId !== writingUser)) {
      throw new Error('Page writing user must match workspace session user')
    }
    if (sessionUserId === null && writingUser) await setWorkspaceSessionUser(scopedDb.driver, writingUser)
    const batchPageIds = new Set(inputs.map(input => input.pageId))
    // Only a root chooses its mode; nested edits join the caller's transaction.
    // Keep bulk creation and batches with additional work on the overlapping path.
    const exclusive = !scopedDb.inTransaction && inputs.length === 1 && !frames[0]?.baseline && !options?.also?.length
    const effects = sourceTransaction && scope ? await (async () => {
      const sourceScope = safe(scope, 'Native source scope required')
      for (let attempt = 1; ; attempt++) {
        try {
          const result = await awaitSourceBatch()
          return result
        } catch (error) {
          if (attempt === 8 || !(error instanceof Error) || !error.message.includes('Page edit source changed; retry')) throw error
        }
      }
      function awaitSourceBatch() {
        return authoredSourceBatch(scopedDb, inputs.map((input, index) => ({ ...input, tel: spanTel, decoded: safe(frames[index]), childPageIds: safe(childPageIds[index]) })), sourceScope, (scope, statements, context) => safe(sourceTransaction)(scope, statements, { ...context, exclusive }), options).then(result => {
          driver.flushInvalidations?.()
          return result
        })
      }
    })() : await scopedDb.transaction(async tx => {
      const effects = []
      for (const [index, input] of inputs.entries()) {
        effects.push(await writePageEdit(tx, { ...input, tel: spanTel, decoded: safe(frames[index]), created: created.get(index) }, batchPageIds))
      }
      for (const statement of options?.also ?? []) await safe(tx.driver).query(statement.sql, statement.params)
      return effects
    }, { exclusive })
    // Bucket effects follow the commit (spaces.spec:213): a pending or failed media call never
    // holds the page transaction, and a rolled-back edit never reaches the bucket. Each call
    // resolves after its meta is written; SyncedBucket pushes in the background and retries
    // (spaces.spec:213/:217), so the committed edit stays a success. Calls fail independently:
    // one refusal must not skip the next call's meta write.
    const apply = async (call: () => Promise<void> | undefined) => {
      try { await call() } catch (error) { spanTel.error(error, { operation: 'page.commit.media' }) }
    }
    for (const [index, media] of effects.entries()) {
      const input = safe(inputs[index])
      for (const src of media.removed) await apply(() => input.bucket?.delete(src))
      for (const src of media.added) await apply(() => input.bucket?.restore(src))
    }
  })
}

/** The decoded form of an empty (fields-only) frame: no baseline, no changes. */
const fieldsOnlyFrame: StateJSON = { header: { kind: 'update', epoch: 0 }, version: {}, changes: [], pending: [] }

async function writePageEdit(tx: WorkspaceDb, input: CommitPageEditInput & { decoded: StateJSON; created?: { doc: ReturnType<typeof crdt.load>; json: ReturnType<ReturnType<typeof crdt.load>['toJSON']> } }, batchPageIds: ReadonlySet<string>) {
  const previous = await tx.query`SELECT ${tx.pages.updatedAt} FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.pageId)}`
    .executeOne({ updatedAt: tx.pages.updatedAt })
  const userId = safe(input.userId, 'Page writing user required')
  const scalarValues = pageScalarValues(input.fields ?? {})
  const changes = {
    ...scalarValues,
    ...(input.fields?.deletedAt === undefined ? {} : { deletedBy: input.fields.deletedAt === null ? null : userId }),
    ...input.columns,
    editedBy: userId,
    data: input.frame,
    updatedAt: new Date(Math.max(input.now.getTime(), (previous?.updatedAt.getTime() ?? -1) + 1)),
  }
  const derivation = { pageId: input.pageId, userId, updatedAt: changes.updatedAt.getTime() }
  if (previous && input.frame.length === 0) {
    const { data: _data, ...fields } = changes
    await tx.query`${tx.pages.update(fields)} WHERE ${tx.pages.id.eq(input.pageId)}`.run()
    await derivePageLifecycle(tx, input.fields?.deletedAt, derivation)
    return { removed: [], added: [] }
  }
  if (previous) {
    // Root and entry scalars are refused on the merged state (assertPageStructure in commitPageEdits, both paths):
    // crdt_rs frames carry no op list to inspect (facade.spec decodeFrame row).
    const decoded = input.decoded
    if (decoded.baseline && decoded.changes.length === 0) {
      assertPageStructure(decoded.baseline.json)
      throw new Error('Pure baseline cannot update an existing page; send authored CRDT edits')
    }
    // The statement's applied buffer survives through RETURNING; a later SELECT does not.
    // The engine rejects missing dependencies before publishing any applied rows.
    const result = await tx.query`${tx.pages.update(changes)} WHERE ${tx.pages.id.eq(input.pageId)}
      RETURNING (SELECT json_group_array(json_object('kind',kind,'node',node,'key',key,'oldValue',old_value,'value',value,'fields',fields,'qid',qid,'entity',entity,'content',content))
        FROM crdt_applied(${input.frame})) AS applied`
      .executeOneOrThrow(appliedSchema)
    // The author's cards travel with the edit (db-crdt-sync requirements :47, :49); receivers derive nothing.
    const rows = parseMutationCdc(result.applied)
    for (const statement of authorMutationWrites(rows, derivation)) await tx.query`${statement}`.run()
    await derivePageLifecycle(tx, input.fields?.deletedAt, derivation)
    const removedQids = new Set<string>()
    const added = new Set<string>(), removed = new Set<string>()
    for (const row of rows) {
      if (row.kind === 'remove' && row.qid) removedQids.add(row.qid)
      // Slash /page can convert an existing paragraph instead of inserting a node.
      // The final applied fields supply identity; createChildPage checks committed rows in this transaction.
      if ((row.kind === 'insert' || row.kind === 'set' && (row.key === 'type' || row.key === 'pageId')) &&
        row.fields.type === 'page' && row.fields.pageId && row.fields.pageId !== input.pageId) {
        await createChildPage(tx, input, row.fields.pageId, userId, batchPageIds)
      }
      if (row.fields.type && mediaTypes.has(row.fields.type)) {
        if (row.kind === 'insert' && row.fields.src) added.add(row.fields.src)
        if (row.kind === 'remove' && row.fields.src) removed.add(row.fields.src)
        if (row.kind === 'set' && row.key === 'src') {
          const old = jsonString(row.oldValue), value = jsonString(row.value)
          if (old) removed.add(old)
          if (value) added.add(value)
        }
      }
      if (row.kind === 'set' && row.key === 'type' && row.fields.src) {
        const old = jsonString(row.oldValue), value = jsonString(row.value)
        if (old && mediaTypes.has(old)) removed.add(row.fields.src)
        if (value && mediaTypes.has(value)) added.add(row.fields.src)
      }
    }
    // Removed question bytes remain readable under page permissions (editor-home.spec:60).
    const retained = removed.size > 0 ? JSON.stringify((await Promise.all([...removedQids].map(qid => removedQuestionParts(tx, input.pageId, qid)))).flat()) : ''
    const addedMedia: string[] = [], removedMedia: string[] = []
    for (const src of removed) {
      if (retained.includes(JSON.stringify(src))) continue
      const reference = await tx.query`SELECT EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',${src}) parts
        JOIN ${tx.pages} p ON p.rowid=parts.pageRowid
        WHERE json_extract(parts.fields,'$.src')=${src} AND json_extract(parts.fields,'$.type') IN ('img','audio','video','file')) AS present`
        .executeOneOrThrow(presentSchema)
      if (!reference.present) removedMedia.push(src)
    }
    for (const src of added) {
      const reference = await tx.query`SELECT EXISTS(SELECT 1 FROM crdt_attr_lookup('idx_pages_crdt_attr',${src},(SELECT rowid FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.pageId)})) parts
        WHERE json_extract(parts.fields,'$.src')=${src} AND json_extract(parts.fields,'$.type') IN ('img','audio','video','file')) AS present`
        .executeOneOrThrow(presentSchema)
      if (reference.present) addedMedia.push(src)
    }
    if (removedMedia.length > 0 && !input.bucket) throw new Error('commitPageEdit requires bucket for media removal')
    return { removed: removedMedia, added: addedMedia }
  }
  // Creation retains snapshot validation and projection; ordinary edits never read a document.
  const merged = input.created?.doc ?? crdt.load(input.frame)
  const mergedJson = input.created?.json ?? merged.toJSON()
  if (!input.created) assertPageStructure(mergedJson)
  const inserted = await tx.query`${tx.pages.insert({ id: input.pageId, ...changes, data: merged.encodeSnapshot().bytes, title: changes.title ?? 'Untitled page', titleKey: changes.titleKey ?? 'untitled page', kind: changes.kind ?? 'note' })} RETURNING (SELECT json_group_array(json_object('kind',kind,'node',node,'key',key,'oldValue',old_value,'value',value,'fields',fields,'qid',qid,'entity',entity,'content',content)) FROM crdt_applied(${input.frame})) AS applied`.executeOneOrThrow(appliedSchema)
  await tx.query`${tx.pagePermissions.insert({ pageId: input.pageId, principalKind: 'user', principalId: userId, level: 3 })}`.run()
  if (input.parentId) await tx.query`${tx.pageRelations.insert({ fromPageId: input.parentId, toPageId: input.pageId, type: 'child' })}`.run()
  if (!changes.deletedAt) for (const statement of authorMutationWrites(parseMutationCdc(inserted.applied), derivation, true)) await tx.query`${statement}`.run()
  // The row now holds `merged`'s snapshot: project its JSON with the row's scalars instead of decoding the row again.
  const document = await readDocument(tx, input.pageId, mergedJson)
  if (document) for (const child of projectPage(input.pageId, document.document).childPages) await createChildPage(tx, input, child.id, userId, batchPageIds)
  return { removed: [], added: [] }
}

/** A soft delete removes the author's cards of the page; a restore derives them again from the page's postings. */
async function derivePageLifecycle(tx: WorkspaceDb, deletedAt: number | null | undefined, derivation: DerivationArgs) {
  for (const statement of pageLifecycleStatements(deletedAt, derivation)) await tx.query`${statement}`.run()
}

/** pageIds of the page blocks in a page JSON value. */
function pageBlockIds(value: unknown, ids = new Set<string>()): Set<string> {
  if (Array.isArray(value)) for (const child of value) pageBlockIds(child, ids)
  else if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (record.type === 'page' && typeof record.pageId === 'string') ids.add(record.pageId)
    for (const child of Object.values(record)) pageBlockIds(child, ids)
  }
  return ids
}

const mediaTypes = new Set(['img','audio','video','file'])
function jsonString(value: string | null) {
  if (value === null) return null
  const parsed: unknown = JSON.parse(value)
  return typeof parsed === 'string' ? parsed : null
}
async function createChildPage(tx: WorkspaceDb, input: CommitPageEditInput, pageId: string, userId: string, batchPageIds: ReadonlySet<string>) {
  // An explicit child edit owns its row and placement; never insert a placeholder ahead of it.
  if (batchPageIds.has(pageId)) return
  if (await tx.query`SELECT ${tx.pages.id} FROM ${tx.pages} WHERE ${tx.pages.id.eq(pageId)}`.executeOne({ id: tx.pages.id })) return
  const title = 'Untitled page'
  const childDoc = newPageDocument({ title, content: { kind: 'note', children: [
    { id: blockId(), type: 'p', children: [] },
  ] }, deletedAt: null }, userId)
  await tx.query`${tx.pages.insert({ id: pageId, data: childDoc.encodeSnapshot().bytes, updatedAt: input.now, title, titleKey: searchKey(title), kind: 'note', editedBy: userId })}`.run()
  await tx.query`${tx.pagePermissions.insert({ pageId, principalKind: 'user', principalId: userId, level: 3 })}`.run()
  await tx.query`${tx.pageRelations.insert({ fromPageId: input.pageId, toPageId: pageId, type: 'child' })}`.run()
}

/** A move publishes the old edge deletion and new edge insertion in one transaction. */
export function movePage(db: WorkspaceDb, input: { pageId: string; parentPageId: string; userId?: string; now: Date; tel: Tel }) {
  return db.transaction(async tx => {
    const moved = await tx.query`SELECT ${tx.pages.kind} FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.pageId)}`.executeOne({ kind: tx.pages.kind })
    if (moved?.kind === 'personalRoot') throw new Error('Unable to move page')
    const row = await tx.query`SELECT ${tx.pageRelations.fromPageId} FROM ${tx.pageRelations}
      WHERE ${tx.pageRelations.toPageId.eq(input.pageId)} AND ${tx.pageRelations.type.eq('child')}`.executeOne({ fromPageId: tx.pageRelations.fromPageId })
    if (!row) throw new Error('Unable to move page')
    // Check committed placement in the writing transaction, before either edge write.
    // Walking upward visits only the destination's ancestry; UNION also bounds corrupt cycles.
    const admission = await tx.query`
      WITH RECURSIVE ancestors(pageId) AS (
        SELECT ${input.parentPageId}
        UNION
        SELECT r.fromPageId FROM ${tx.pageRelations} r JOIN ancestors a ON r.toPageId=a.pageId
        WHERE r.type='child'
      )
      SELECT EXISTS(SELECT 1 FROM ${tx.pages} WHERE ${tx.pages.id.eq(input.parentPageId)} AND ${tx.pages.deletedAt.isNull()})
        AND NOT EXISTS(SELECT 1 FROM ancestors WHERE pageId=${input.pageId}) AS admitted
    `.executeOneOrThrow(admittedSchema)
    if (!admission.admitted) throw new Error('Unable to move page')
    if (row.fromPageId === input.parentPageId) return
    await tx.query`DELETE FROM ${tx.pageRelations} WHERE ${tx.pageRelations.toPageId.eq(input.pageId)} AND ${tx.pageRelations.type.eq('child')}`.run()
    await tx.query`${tx.pageRelations.insert({ fromPageId: input.parentPageId, toPageId: input.pageId, type: 'child' })}`.run()
  })
}

async function readDocument(db: WorkspaceDb, pageId: string, json?: unknown) {
  const row = await db.query`SELECT ${pageSourceColumns(db.pages)} FROM ${db.pages} WHERE ${db.pages.id.eq(pageId)}`.executeOne(db.pages.__selectSchema)
  return row ? { row, document: readPageDocument(json ?? crdt.load(row.data).toJSON(), row), updatedAt: row.updatedAt, frame: Uint8Array.from(row.data) } : null
}

export async function loadPageDocument(db: WorkspaceDb, pageId: string) {
  const row = await readDocument(db, pageId)
  return row?.document ?? null
}
